import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Modal, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus as FaPlus, Pencil as FaEdit, Trash2 as FaTrash, GraduationCap as FaUserGraduate, UserRound as FaUserTie, Unlink as FaUnlink, Link2 as FaLink } from 'lucide-react';
import api from '../../api/axios';
import Loader from '../../components/Loader';
import GradientButton from '../../components/GradientButton';
import { FEATURES } from '../../config/features';

// Typ „Uczeń / młodociany” ukryty przez FEATURES.youth — istniejący uczniowie zachowują etykietę
const DEFAULT_WORKER_TYPE = FEATURES.youth ? 'apprentice' : 'employee';

function EmployerEmployees() {
  const navigate = useNavigate();

  const [workers, setWorkers] = useState([]);
  const [slots, setSlots] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workerToDelete, setWorkerToDelete] = useState(null);

  const [formData, setFormData] = useState({
    worker_type: DEFAULT_WORKER_TYPE,
    first_name: '',
    last_name: '',
    birth_date: '',
    profession: '',
    notes: '',
    vacation_days_total: 0,
  });

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [managedRes, linkedRes, slotsRes] = await Promise.all([
        api.get('/employer/workers/'),
        api.get('/employer/linked-workers/accepted/'),
        api.get('/employer/slots/'),
      ]);

      const managed = (Array.isArray(managedRes.data) ? managedRes.data : (managedRes.data?.results ?? []))
        .map(w => ({ ...w, source: 'managed' }));

      const linked = (linkedRes.data || []).map(link => ({
        id: link.id,
        source: 'linked',
        worker_type: 'employee',
        first_name: link.worker_first_name,
        last_name: link.worker_last_name,
        birth_date: link.worker_birth_date,
        profession: '',
        notes: '',
        vacation_days_total: link.vacation_days_total,
        vacation_days_used: link.vacation_days_used,
        vacation_days_available: link.vacation_days_available,
      }));

      setWorkers([...managed, ...linked]);
      setSlots(slotsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      worker_type: DEFAULT_WORKER_TYPE,
      first_name: '',
      last_name: '',
      birth_date: '',
      profession: '',
      notes: '',
      vacation_days_total: 0,
    });
    setEditingWorker(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const handleOpenEdit = (worker) => {
    if (worker.source === 'linked') {
      setFormData(prev => ({ ...prev, vacation_days_total: worker.vacation_days_total || 0 }));
      setEditingWorker(worker);
      setShowModal(true);
      return;
    }
    setFormData({
      worker_type: worker.worker_type,
      first_name: worker.first_name,
      last_name: worker.last_name,
      birth_date: worker.birth_date,
      profession: worker.profession || '',
      notes: worker.notes || '',
      vacation_days_total: worker.vacation_days_total || 0,
    });
    setEditingWorker(worker);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingWorker?.source === 'linked') {
        await api.put(`/employer/linked-workers/${editingWorker.id}/vacation-pool/`, {
          vacation_days_total: formData.vacation_days_total,
        });
        setSuccess('Pula urlopowa została zaktualizowana');
      } else if (editingWorker) {
        await api.put(`/employer/workers/${editingWorker.id}/`, formData);
        setSuccess('Pracownik został zaktualizowany');
      } else {
        await api.post('/employer/workers/add/', formData);
        setSuccess('Pracownik został dodany');
      }
      setShowModal(false);
      resetForm();
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Wystąpił błąd. Sprawdź wprowadzone dane.');
    }
  };

  const handleDeleteClick = (worker) => {
    setWorkerToDelete(worker);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (workerToDelete.source === 'linked') {
        await api.post(`/vacation/links/${workerToDelete.id}/unlink/`);
        setSuccess('Pracownik został rozłączony');
      } else {
        await api.delete(`/employer/workers/${workerToDelete.id}/`);
        setSuccess('Pracownik został usunięty');
      }
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Nie udało się usunąć pracownika');
    }
    setShowDeleteModal(false);
    setWorkerToDelete(null);
  };

  if (loading) return <Loader />;

  const canAddMore = slots && slots.used_worker_slots < slots.total_worker_slots;

  return (
    <div style={{ minHeight: '100vh', paddingTop: '2rem', paddingBottom: '4rem' }}>
      <Container fluid className="px-2 px-md-4">

        <motion.div {...fadeInUp} className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
          <div>
            <h1 className="pub-h2 mb-1">Pracownicy</h1>
            <p className="pub-muted mb-0">
              {slots?.used_worker_slots || 0} / {slots?.total_worker_slots || 1} slotów wykorzystanych
            </p>
          </div>
          <GradientButton
            gradient="blue"
            onClick={canAddMore ? handleOpenAdd : () => navigate('/employer-app-pricing')}
          >
            <FaPlus className="me-2" />
            {canAddMore ? 'Dodaj pracownika' : 'Dokup sloty'}
          </GradientButton>
        </motion.div>

        {success && <Alert variant="success" className="border-0" style={{ borderRadius: '15px' }}>{success}</Alert>}
        {error && !showModal && <Alert variant="danger" className="border-0" style={{ borderRadius: '15px' }}>{error}</Alert>}

        {!canAddMore && (
          <Alert variant="warning" className="border-0 mb-4" style={{ borderRadius: '15px' }}>
            Osiągnięto limit pracowników w Twoim planie. <a href="/employer-app-pricing" className="fw-bold">Dokup dodatkowe sloty</a> aby dodać więcej.
          </Alert>
        )}

        {workers.length === 0 ? (
          <motion.div {...fadeInUp}>
            <Card className="text-center">
              <Card.Body className="p-5">
                <h3 className="pub-h3 mb-3">Brak pracowników</h3>
                <p className="pub-muted mb-4">{FEATURES.youth ? 'Dodaj pierwszego pracownika lub ucznia, żeby zacząć zarządzać urlopami.' : 'Dodaj pierwszego pracownika, żeby zacząć zarządzać urlopami.'}</p>
                <GradientButton gradient="blue" onClick={handleOpenAdd}>
                  <FaPlus className="me-2" /> Dodaj pracownika
                </GradientButton>
              </Card.Body>
            </Card>
          </motion.div>
        ) : (
          <Row className="g-4">
            <AnimatePresence>
              {workers.map((worker, idx) => (
                <Col key={`${worker.source}-${worker.id}`} xs={12} md={6} lg={4}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Card className="h-100" style={{ overflow: 'hidden' }}>
                      <div
                        className="p-3 d-flex align-items-center gap-3"
                        style={{ background: 'var(--pub-bg-alt)', borderBottom: '1px solid var(--pub-line-soft)', color: 'var(--pub-navy)' }}
                      >
                        {worker.worker_type === 'apprentice'
                          ? <FaUserGraduate size={24} />
                          : <FaUserTie size={24} />}
                        <div>
                          <h5 className="mb-0 fw-bold">{worker.first_name} {worker.last_name}</h5>
                          <small style={{ opacity: 0.85, display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {worker.source === 'linked'
                              ? 'Połączone konto'
                              : (worker.worker_type === 'apprentice' ? 'Uczeń / młodociany' : 'Pracownik dorosły')}
                            {worker.source === 'linked' && <FaLink size={10} />}
                          </small>
                        </div>
                      </div>
                      <Card.Body className="p-3">
                        {worker.profession && (
                          <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                            <strong>Zawód:</strong> {worker.profession}
                          </p>
                        )}
                        <Row className="g-2 text-center mb-3">
                          <Col xs={4}>
                            <div className="py-2 px-1" style={{ background: '#f1f3f5', borderRadius: '12px' }}>
                              <div className="fw-bold" style={{ fontSize: '1.3rem', color: '#212529' }}>{worker.vacation_days_total}</div>
                              <small style={{ color: '#6c757d', fontSize: '0.75rem' }}>Przyznane</small>
                            </div>
                          </Col>
                          <Col xs={4}>
                            <div className="py-2 px-1" style={{ background: '#fff3e0', borderRadius: '12px' }}>
                              <div className="fw-bold" style={{ fontSize: '1.3rem', color: '#e67700' }}>{worker.vacation_days_used}</div>
                              <small style={{ color: '#6c757d', fontSize: '0.75rem' }}>Użyte</small>
                            </div>
                          </Col>
                          <Col xs={4}>
                            <div className="py-2 px-1" style={{ background: '#e8f5e9', borderRadius: '12px' }}>
                              <div className="fw-bold" style={{ fontSize: '1.3rem', color: '#2b8a3e' }}>{worker.vacation_days_available}</div>
                              <small style={{ color: '#6c757d', fontSize: '0.75rem' }}>Dostępne</small>
                            </div>
                          </Col>
                        </Row>
                        <div className="d-flex gap-2 align-items-stretch">
                          <button
                            onClick={() => navigate(`/employer-vacations?worker=${worker.id}&source=${worker.source}`)}
                            className="btn flex-grow-1"
                            style={{
                              borderRadius: '10px',
                              background: 'var(--pub-navy)',
                              color: 'white',
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              padding: '0.5rem 0.75rem',
                              border: 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Urlopy
                          </button>
                          <button
                            onClick={() => handleOpenEdit(worker)}
                            className="btn btn-outline-secondary"
                            style={{ borderRadius: '10px', flexShrink: 0, width: '42px' }}
                            title={worker.source === 'linked' ? 'Edytuj pulę urlopową' : 'Edytuj'}
                          >
                            <FaEdit size={14} />
                          </button>
                          {worker.source === 'linked' ? (
                            <button
                              onClick={() => handleDeleteClick(worker)}
                              className="btn btn-outline-warning"
                              style={{ borderRadius: '10px', flexShrink: 0, width: '42px' }}
                              title="Rozłącz"
                            >
                              <FaUnlink size={14} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeleteClick(worker)}
                              className="btn btn-outline-danger"
                              style={{ borderRadius: '10px', flexShrink: 0, width: '42px' }}
                              title="Usuń"
                            >
                              <FaTrash size={14} />
                            </button>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </AnimatePresence>
          </Row>
        )}

        {/* MODAL DODAWANIA/EDYCJI */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
          <Modal.Header closeButton style={{ border: 'none' }}>
            <Modal.Title>
              {editingWorker?.source === 'linked'
                ? `Pula urlopowa — ${editingWorker.first_name} ${editingWorker.last_name}`
                : (editingWorker ? 'Edytuj pracownika' : 'Dodaj pracownika')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {error && <Alert variant="danger" className="border-0" style={{ borderRadius: '12px' }}>{error}</Alert>}

            {editingWorker?.source === 'linked' ? (
              // ─── FORMULARZ DLA POŁĄCZONEGO KONTA — tylko pula urlopowa ──────
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-4">
                  <Form.Label className="fw-medium">Przyznane dni urlopu</Form.Label>
                  <Form.Control
                    type="number" min="0" max="365"
                    name="vacation_days_total" value={formData.vacation_days_total}
                    onChange={handleChange} required style={{ borderRadius: '12px', maxWidth: '200px' }}
                  />
                  <Form.Text className="text-muted">
                    To konto jest połączone z realnym profilem pracownika — dane osobowe (imię, nazwisko, data urodzenia) zarządza on sam w swoim profilu.
                  </Form.Text>
                </Form.Group>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-light" onClick={() => setShowModal(false)} style={{ borderRadius: '12px' }}>
                    Anuluj
                  </button>
                  <GradientButton type="submit" gradient="blue">
                    Zapisz pulę
                  </GradientButton>
                </div>
              </Form>
            ) : (
              // ─── FORMULARZ DLA RĘCZNIE DODANEGO PRACOWNIKA — pełny ──────────
              <Form onSubmit={handleSubmit}>

                {FEATURES.youth && (
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Typ pracownika</Form.Label>
                  <div className="d-flex gap-3">
                    {[
                      { val: 'apprentice', label: 'Uczeń / młodociany', icon: <FaUserGraduate /> },
                      { val: 'employee', label: 'Pracownik dorosły', icon: <FaUserTie /> },
                    ].map(opt => (
                      <div
                        key={opt.val}
                        onClick={() => setFormData(prev => ({ ...prev, worker_type: opt.val }))}
                        className="flex-fill p-3 text-center"
                        style={{
                          borderRadius: '14px', cursor: 'pointer',
                          border: formData.worker_type === opt.val ? '2px solid #0d6efd' : '2px solid #e9ecef',
                          background: formData.worker_type === opt.val ? 'rgba(13,110,253,0.08)' : 'transparent',
                        }}
                      >
                        <div className="mb-1">{opt.icon}</div>
                        <small className="fw-medium">{opt.label}</small>
                      </div>
                    ))}
                  </div>
                </Form.Group>
                )}

                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Imię</Form.Label>
                      <Form.Control
                        type="text" name="first_name" value={formData.first_name}
                        onChange={handleChange} required style={{ borderRadius: '12px' }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Nazwisko</Form.Label>
                      <Form.Control
                        type="text" name="last_name" value={formData.last_name}
                        onChange={handleChange} required style={{ borderRadius: '12px' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Data urodzenia</Form.Label>
                      <Form.Control
                        type="date" name="birth_date" value={formData.birth_date}
                        onChange={handleChange} required style={{ borderRadius: '12px' }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Zawód (opcjonalnie)</Form.Label>
                      <Form.Control
                        type="text" name="profession" value={formData.profession}
                        onChange={handleChange} style={{ borderRadius: '12px' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Przyznane dni urlopu</Form.Label>
                  <Form.Control
                    type="number" min="0" max="365"
                    name="vacation_days_total" value={formData.vacation_days_total}
                    onChange={handleChange} required style={{ borderRadius: '12px', maxWidth: '200px' }}
                  />
                  <Form.Text className="text-muted">
                    Wpisz pulę urlopową przyznaną temu pracownikowi na bieżący rok.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-medium">Notatki (opcjonalnie)</Form.Label>
                  <Form.Control
                    as="textarea" rows={2} name="notes" value={formData.notes}
                    onChange={handleChange} style={{ borderRadius: '12px' }}
                  />
                </Form.Group>

                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-light" onClick={() => setShowModal(false)} style={{ borderRadius: '12px' }}>
                    Anuluj
                  </button>
                  <GradientButton type="submit" gradient="blue">
                    {editingWorker ? 'Zapisz zmiany' : 'Dodaj pracownika'}
                  </GradientButton>
                </div>
              </Form>
            )}
          </Modal.Body>
        </Modal>

        {/* MODAL USUWANIA / ROZŁĄCZANIA */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton style={{ border: 'none' }}>
            <Modal.Title>
              {workerToDelete?.source === 'linked' ? 'Potwierdzenie rozłączenia' : 'Potwierdzenie usunięcia'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <p>
              Czy na pewno chcesz {workerToDelete?.source === 'linked' ? 'rozłączyć' : 'usunąć'} pracownika{' '}
              <strong>{workerToDelete?.first_name} {workerToDelete?.last_name}</strong>?
            </p>
            <p className="text-danger small fw-medium">
              {workerToDelete?.source === 'linked'
                ? 'Pracownik straci dostęp do składania wniosków urlopowych u Ciebie — będzie mógł połączyć się ponownie w przyszłości.'
                : 'Ta operacja jest nieodwracalna — wszystkie dane i historia urlopów tego pracownika zostaną trwale usunięte.'}
            </p>
          </Modal.Body>
          <Modal.Footer style={{ border: 'none' }}>
            <button className="btn btn-light" onClick={() => setShowDeleteModal(false)} style={{ borderRadius: '12px' }}>
              Anuluj
            </button>
            <button className="btn btn-danger" onClick={handleConfirmDelete} style={{ borderRadius: '12px' }}>
              {workerToDelete?.source === 'linked' ? 'Rozłącz' : 'Usuń trwale'}
            </button>
          </Modal.Footer>
        </Modal>

      </Container>
    </div>
  );
}

export default EmployerEmployees;